import { formatNumber } from '@/utils/formatNumber'
import { clsx } from 'clsx'
import { ReactNode, useMemo } from 'react'
import { Bar, BarChart, CartesianGrid, ReferenceLine, ResponsiveContainer, Text, Tooltip, XAxis, YAxis } from 'recharts'
import { useUiKit } from '../providers'
import styles from './Chart.module.scss'
import { Text as UiKitText } from './Text'

interface ChartProps {
  data: { title: string | number; value: number; value2?: number; value3?: number; value4?: number }[]
  formatXAxisTitle?: (value: string, index: number) => string | number
  formatTooltipTitle?: (value: string) => string
  formatTooltipDescription?: (value: string) => ReactNode
  referenceLineValue?: string
  isPercents?: boolean
  className?: string
  tooltipNode?: ReactNode
}

export const Chart = ({
  data,
  formatXAxisTitle,
  formatTooltipTitle,
  formatTooltipDescription,
  referenceLineValue,
  isPercents,
  className,
  tooltipNode
}: ChartProps) => {
  const isStackedBars = useMemo(() => data.some(({ value3 }) => value3 !== undefined), [data])
  const { theme = { colorGray100to70: '', color: '', colorLightYellow: '', colorYellow: '' }, isThemeDark } = useUiKit()

  const maxValue = useMemo(() => {
    const sortedByValue = [...data].sort(
      (item1, item2) => item1.value + (item1.value2 || 0) - item2.value - (item2.value2 || 0)
    )
    const maxV = Math.round(sortedByValue.at(-1)?.value || 0 + (sortedByValue.at(-1)?.value2 || 0)) + 1

    return maxV
  }, [data])

  return (
    <ResponsiveContainer className={clsx('-mb-4', className)} height={275} width="100%">
      <BarChart data={data}>
        <CartesianGrid
          stroke={theme.colorGray100to70}
          strokeDasharray="" // Keep "" for solid lines (not dashed)
          horizontal={true}
          vertical={false}
        />
        <Tooltip
          formatter={formatTooltipDescription}
          content={
            (tooltipNode as any) ||
            ((props) => (
              <CustomTooltip
                {...props}
                formatTooltipTitle={formatTooltipTitle}
                formatTooltipDescription={formatTooltipDescription}
                isStackedBars={isStackedBars}
              />
            ))
          }
          cursor={{ fill: theme.colorGray100to70 + (isThemeDark ? '99' : '77') }}
        />
        <YAxis
          tick={Tick}
          stroke={theme.colorGray100to70}
          tickMargin={8}
          width={Math.max(maxValue.toString().length * 10, 40)}
          domain={isPercents ? [0, 100] : ['auto', 'auto']}
          tickCount={Math.max(2.5, Math.min(maxValue, 5))}
        />
        <XAxis
          dataKey="title"
          stroke={theme.colorGray100to70}
          tick={(props) => Tick({ ...props, formatXAxisTitle })}
          tickLine={false}
          tickMargin={0}
          interval={0}
        />
        <Bar
          stackId="bar1"
          dataKey="value"
          fill={theme.color}
          radius={data[0].value3 !== undefined || data[0].value2 !== undefined ? [0, 0, 4, 4] : 4}
        />
        <Bar
          stackId="bar1"
          dataKey="value2"
          fill={'#7EED52'}
          radius={data[0].value3 !== undefined ? 0 : [4, 4, 0, 0]}
        />
        <Bar
          stackId="bar1"
          dataKey="value3"
          fill={theme.colorLightYellow}
          radius={data[0].value !== undefined ? [4, 4, 0, 0] : 4}
        />
        <Bar
          stackId="bar1"
          dataKey="value4"
          fill={'#35FFE4'}
          radius={data[0].value4 !== undefined ? [4, 4, 0, 0] : 4}
        />
        {referenceLineValue && <ReferenceLine x={referenceLineValue} stroke={theme.colorYellow} />}
      </BarChart>
    </ResponsiveContainer>
  )
}

function CustomTooltip({
  active,
  payload,
  formatTooltipTitle,
  formatTooltipDescription,
  label,
  isStackedBars
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
}: any &
  ChartProps['formatTooltipTitle'] &
  ChartProps['formatTooltipDescription'] & { isStackedBars?: boolean }): React.JSX.Element | null {
  const value = payload[0]?.payload?.value || 0
  const value2 = payload[0]?.payload?.value2 || 0
  const value3 = payload[0]?.payload?.value3 || 0
  const value4 = payload[0]?.payload?.value4 || 0

  return active && payload?.length ? (
    <div className="backgroundColorGray100to70 flex flex-col rounded-lg px-3 py-2">
      {isStackedBars ? (
        <ul className="mb-1">
          <li>
            <UiKitText className="whitespace-nowrap" type="body1-semibold">
              DataHive Points: {formatNumber(value + value2 + value3, 0)}
            </UiKitText>
          </li>
          <li className="mt-0.5 flex items-center gap-2">
            {value2 > 0 ? (
              <div className="relative h-3 w-5">
                <div className="absolute -top-[1px] z-[1] h-[14px] w-[14px] overflow-hidden rounded-[3px] border border-solid border-[--colorGray100to70] bg-[#ff9736]"></div>
                <div className="absolute left-2.5 top-0 h-3 w-3 overflow-hidden rounded-[3px] bg-[#7EED52]"></div>
              </div>
            ) : (
              <div className="backgroundColor h-3 w-3 rounded-[3px]" />
            )}
            <UiKitText className="whitespace-nowrap" type="body3-regular">
              Data Points: {formatNumber(value + value2, 0)}
            </UiKitText>
          </li>
          {value2 > 0 && (
            <ul className="pl-5">
              <li className="mt-0.5 flex items-center gap-2">
                <div className="backgroundColor h-3 w-3 rounded-[3px]" />
                <UiKitText className="whitespace-nowrap" type="body3-regular">
                  Basic: {formatNumber(value, 0)}
                </UiKitText>
              </li>
              <li className="mt-0.5 flex items-center gap-2">
                <div className="h-3 w-3 rounded-[3px] bg-[#7EED52]" />
                <UiKitText className="whitespace-nowrap" type="body3-regular">
                  Restricted: {formatNumber(value2, 0)}
                </UiKitText>
              </li>
            </ul>
          )}
          <li className="flex items-center gap-2">
            <div className="backgroundColorLightYellow h-3 w-3 rounded-[3px]" />
            <UiKitText className="whitespace-nowrap" type="body3-regular">
              Hive Points: {formatNumber(value3, 0)}
            </UiKitText>
          </li>
          <li className="flex items-center gap-2">
            <div className="h-3 w-3 rounded-[3px] bg-[#35FFE4]" />
            <UiKitText className="whitespace-nowrap" type="body3-regular">
              Referral Points: {formatNumber(value4, 0)}
            </UiKitText>
          </li>
        </ul>
      ) : (
        <UiKitText type="body1-semibold">{formatTooltipTitle ? formatTooltipTitle(value) : value}</UiKitText>
      )}
      <UiKitText className="colorGray80to90 flex flex-col" type="body3-regular">
        {formatTooltipDescription ? formatTooltipDescription(label) : label}
      </UiKitText>
    </div>
  ) : null
}

export const JobsTooltip = ({
  active,
  payload,
  formatter,
  label
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
}: any) => {
  const value = payload[0]?.payload?.value || 0
  const value2 = payload[0]?.payload?.value2 || 0

  return active && payload?.length ? (
    <div className="backgroundColorGray100to70 flex flex-col rounded-lg px-3 py-2">
      <ul className="mb-1">
        <li>
          <UiKitText className="whitespace-nowrap" type="body1-semibold">
            Jobs: {formatNumber(value + value2, 0)}
          </UiKitText>
        </li>
        {value2 > 0 && (
          <>
            <li className="mt-0.5 flex items-center gap-2">
              <div className="backgroundColor h-3 w-3 rounded-[3px]" />
              <UiKitText className="whitespace-nowrap" type="body3-regular">
                Basic: {formatNumber(value, 0)}
              </UiKitText>
            </li>
            <li className="mt-0.5 flex items-center gap-2">
              <div className="h-3 w-3 rounded-[3px] bg-[#7EED52]" />
              <UiKitText className="whitespace-nowrap" type="body3-regular">
                Restricted: {formatNumber(value2, 0)}
              </UiKitText>
            </li>
          </>
        )}
      </ul>
      <UiKitText className="colorGray80to90 flex flex-col" type="body3-regular">
        {formatter ? formatter(label) : label}
      </UiKitText>
    </div>
  ) : null
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Tick = (props: any & ChartProps['formatXAxisTitle']) => {
  const {
    payload: { value },
    formatXAxisTitle
  } = props

  props.className = `${props.className} ${styles.AxisText} select-none`

  return (
    <Text {...props}>
      {props.orientation === 'left' ? formatNumber(value, 0) : (formatXAxisTitle?.(value) ?? value)}
    </Text>
  )
}
